// Document Service for Core Backend Integration
// This service handles document uploads, downloads, and management via the backend API
import { TokenStorage } from '../utils/token-storage';

export interface DocumentMetadata {
  id: string;
  fileName: string;
  originalName?: string;
  fileType: string;
  documentType: string;
  notes?: string;
  uploadedBy: string;
  uploadedAt: string;
  fileSize: number;
  gcpBucketPath: string;
  downloadUrl?: string;
}

export interface UploadResponse {
  success: boolean;
  documentId?: string;
  downloadUrl?: string;
  error?: string;
}

export interface DeleteResponse {
  success: boolean;
  error?: string;
}

class DocumentService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = process.env.REACT_APP_CORE_BACKEND_ENDPOINT || 'http://localhost:8210';
  }

  /**
   * Get authentication token from localStorage
   */
  private getAuthToken(): string | null {
    return TokenStorage.getAccessToken();
  }

  /**
   * Make authenticated API request
   */
  private async makeRequest(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error('No authentication token found. Please log in again.');
    }

    console.log(`Making request to: ${this.baseUrl}${endpoint}`);
    
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    console.log(`Response status: ${response.status} for ${endpoint}`);

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      }
      if (response.status === 403) {
        throw new Error('Access denied. You do not have permission to perform this action.');
      }
      if (response.status === 404) {
        throw new Error('Resource not found. The requested endpoint does not exist.');
      }
      if (response.status >= 500) {
        throw new Error('Server error. Please try again later.');
      }
      
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response;
  }

  /**
   * Upload a document to the backend
   * @param file - The file to upload
   * @param documentType - Type of medical document
   * @param notes - Optional notes about the document
   * @param appointmentId - ID of the appointment
   * @param uploadedBy - User who uploaded the document
   */
  async uploadDocument(
    file: File,
    documentType: string,
    notes: string,
    appointmentId: string,
    uploadedBy: string
  ): Promise<UploadResponse> {
    try {
      console.log(`Uploading document: ${file.name} (${file.size} bytes) for appointment ${appointmentId}`);
      
      // Process file for UTF-8 compatibility
      const processedFile = this.processFileForUTF8(file);
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', processedFile);
      formData.append('appointmentId', appointmentId);
      formData.append('documentType', documentType);
      if (notes) {
        formData.append('notes', notes);
      }

      const response = await this.makeRequest('/appointment-documents/upload', {
        method: 'POST',
        body: formData,
        // Don't set Content-Type header for FormData, let the browser set it
      });

      const result = await response.json();
      console.log('Upload response:', result);

      if (result.id) {
        return {
          success: true,
          documentId: result.id,
          downloadUrl: result.downloadUrl
        };
      } else {
        return {
          success: false,
          error: 'Upload failed - no document ID returned'
        };
      }
    } catch (error) {
      console.error('Document upload error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Delete a document via the backend
   * @param documentId - ID of the document to delete
   * @param gcpBucketPath - Path of the file in GCP bucket (not used in current backend implementation)
   */
  async deleteDocument(documentId: string, gcpBucketPath: string): Promise<DeleteResponse> {
    try {
      const response = await this.makeRequest(`/appointment-documents/${documentId}`, {
        method: 'DELETE',
        // DELETE requests typically don't have a body
      });

      const result = await response.json();

      if (result.success !== false) {
        return { success: true };
      } else {
        return {
          success: false,
          error: result.message || 'Delete failed'
        };
      }
    } catch (error) {
      console.error('Document delete error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Get download URL for a document
   * @param gcpBucketPath - Path of the file in GCP bucket
   * @param expiresIn - Expiration time in seconds (default: 1 hour)
   */
  async getDownloadUrl(gcpBucketPath: string, expiresIn: number = 3600): Promise<string> {
    try {
      // For now, return the direct path since the backend stores files locally
      // In a production environment, this would generate signed URLs
      return `${this.baseUrl}/appointment-documents/download/${gcpBucketPath.split('/').pop()}`;
    } catch (error) {
      console.error('Error generating download URL:', error);
      throw new Error('Failed to generate download URL');
    }
  }

  /**
   * List documents for an appointment
   * @param appointmentId - ID of the appointment
   */
  async listDocuments(appointmentId: string): Promise<DocumentMetadata[]> {
    try {
      const response = await this.makeRequest(`/appointment-documents/appointment/${appointmentId}`);
      const documents = await response.json();

      // Transform backend response to match our DocumentMetadata interface
      return documents.map((doc: any) => ({
        id: doc.id,
        fileName: doc.file_name || doc.bucketPath?.split('/').pop() || 'Unknown file',
        originalName: doc.original_name || doc.fileName || doc.bucketPath?.split('/').pop() || 'Unknown file',
        fileType: doc.mime_type || doc.fileType,
        documentType: doc.document_type,
        notes: doc.notes,
        uploadedBy: doc.uploaded_by,
        uploadedAt: doc.created_at || doc.uploaded_at,
        fileSize: doc.size,
        gcpBucketPath: doc.bucket_path,
        downloadUrl: doc.download_url
      }));
    } catch (error) {
      console.error('Error listing documents:', error);
      return [];
    }
  }

  /**
   * Process file for UTF-8 compatibility
   * @param file - File to process
   * @returns Processed file with UTF-8 compatible filename
   */
  processFileForUTF8(file: File): File {
    try {
      // Decode the filename if it's URL encoded
      let decodedName = file.name;
      try {
        decodedName = decodeURIComponent(file.name);
      } catch {
        // If decodeURIComponent fails, use the original name
        decodedName = file.name;
      }
      
      // Remove any null bytes or control characters
      decodedName = decodedName.replace(/[\x00-\x1f\x7f]/g, '');
      
      // Remove any potentially dangerous characters for filenames
      decodedName = decodedName.replace(/[<>:"/\\|?*]/g, '_');
      
      // Ensure it's a valid UTF-8 string
      decodedName = decodedName.normalize('NFC');
      
      // Create a new File object with the processed name
      return new File([file], decodedName, {
        type: file.type,
        lastModified: file.lastModified
      });
    } catch (error) {
      console.warn('Error processing filename for UTF-8:', error);
      // Return original file if processing fails
      return file;
    }
  }

  /**
   * Validate file before upload
   * @param file - File to validate
   */
  validateFile(file: File): { isValid: boolean; error?: string } {
    // Check file size (50MB limit to match backend)
    if (file.size > 50 * 1024 * 1024) {
      return {
        isValid: false,
        error: 'File must be smaller than 50MB'
      };
    }

    // Process filename for UTF-8 compatibility before validation
    const processedFile = this.processFileForUTF8(file);
    
    // Check file type (matching backend validation)
    const allowedTypes = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png', 'gif', 'txt'];
    const fileExtension = processedFile.name.split('.').pop()?.toLowerCase();
    
    if (!fileExtension || !allowedTypes.includes(fileExtension)) {
      return {
        isValid: false,
        error: 'Unsupported file type. Please upload PDF, DOC, DOCX, JPG, PNG, GIF, or TXT files.'
      };
    }

    return { isValid: true };
  }
}

export const documentService = new DocumentService();
export default documentService;
